import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { 
  Users, MapPin, Clock, DollarSign, Bell, Shield, Car, 
  AlertTriangle, CheckCircle, Eye, Settings, Plus, Trash2,
  Navigation, Phone, Star, Calendar, Ban, Home, School,
  FileText, TrendingUp, TrendingDown
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parent Dashboard Main Component
const ParentDashboard = ({ token, user }) => {
  const [teens, setTeens] = useState([]);
  const [selectedTeen, setSelectedTeen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchTeens();
    fetchNotifications();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      fetchTeens();
      fetchNotifications();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchTeens = async () => {
    try {
      const res = await fetch(`${API}/parent/teens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTeens(data.teens || []);
      if (data.teens?.length > 0 && !selectedTeen) {
        setSelectedTeen(data.teens[0]);
      }
    } catch (error) {
      console.error('Error fetching teens:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API}/parent/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-[#1E4DB7] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (teens.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <Card className="max-w-md mx-auto mt-20">
          <CardContent className="p-8 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">No Teen Accounts Linked</h2>
            <p className="text-slate-500 mb-4">
              When your teen creates an account with your email as the parent contact, 
              they will appear here.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#1E4DB7] text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Parent Dashboard</h1>
            <p className="text-white/70 text-sm">Monitor and manage teen rider accounts</p>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/10 relative"
            >
              <Bell className="w-5 h-5" />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                  {notifications.filter(n => !n.read).length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Teen Selector */}
        {teens.length > 1 && (
          <div className="mb-6">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {teens.map((teen) => (
                <Button
                  key={teen.id}
                  variant={selectedTeen?.id === teen.id ? "default" : "outline"}
                  onClick={() => setSelectedTeen(teen)}
                  className={`shrink-0 ${selectedTeen?.id === teen.id ? 'bg-[#1E4DB7]' : ''}`}
                >
                  <Users className="w-4 h-4 mr-2" />
                  {teen.name}
                  {teen.active_ride && (
                    <Badge className="ml-2 bg-green-500">Active</Badge>
                  )}
                </Button>
              ))}
            </div>
          </div>
        )}

        {selectedTeen && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="locations">Locations</TabsTrigger>
              <TabsTrigger value="limits">Limits</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
              <TabsTrigger value="report">Report Card</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <TeenOverview teen={selectedTeen} token={token} onUpdate={fetchTeens} />
            </TabsContent>

            <TabsContent value="locations">
              <ApprovedLocations teenId={selectedTeen.id} token={token} />
            </TabsContent>

            <TabsContent value="limits">
              <SpendingLimits teenId={selectedTeen.id} token={token} />
            </TabsContent>

            <TabsContent value="history">
              <RideHistory teenId={selectedTeen.id} teenName={selectedTeen.name} token={token} />
            </TabsContent>

            <TabsContent value="report">
              <WeeklyReport teenId={selectedTeen.id} token={token} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

// Teen Overview Component
const TeenOverview = ({ teen, token, onUpdate }) => {
  const [liveLocation, setLiveLocation] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchLiveLocation();
    fetchSettings();
    
    const interval = setInterval(fetchLiveLocation, 10000);
    return () => clearInterval(interval);
  }, [teen.id]);

  const fetchLiveLocation = async () => {
    try {
      const res = await fetch(`${API}/parent/teen/${teen.id}/live-location`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLiveLocation(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/parent/teen/${teen.id}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSettings(data.settings);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const toggleRestriction = async (restrict) => {
    try {
      const res = await fetch(`${API}/parent/teen/${teen.id}/restrict?restrict=${restrict}&reason=Manual restriction by parent`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(restrict ? 'Account restricted' : 'Restriction removed');
        fetchSettings();
        onUpdate();
      }
    } catch (error) {
      toast.error('Failed to update restriction');
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#1E4DB7]" />
            Current Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {liveLocation?.has_active_ride ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Car className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-700">
                    {teen.name} is on a ride
                  </span>
                </div>
                <div className="space-y-2 text-sm text-green-600">
                  <p><strong>Status:</strong> {liveLocation.ride?.status}</p>
                  <p><strong>Driver:</strong> {liveLocation.ride?.driver_name || 'Finding driver...'}</p>
                  <p><strong>From:</strong> {liveLocation.ride?.pickup_location?.address}</p>
                  <p><strong>To:</strong> {liveLocation.ride?.dropoff_location?.address}</p>
                </div>
              </div>
              {liveLocation.ride?.driver_phone && (
                <Button variant="outline" className="w-full" asChild>
                  <a href={`tel:${liveLocation.ride.driver_phone}`}>
                    <Phone className="w-4 h-4 mr-2" /> Call Driver
                  </a>
                </Button>
              )}
            </div>
          ) : (
            <div className="p-4 bg-slate-50 rounded-xl text-center">
              <CheckCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-slate-600">{teen.name} is not currently on a ride</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restriction Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#1E4DB7]" />
            Account Controls
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings?.is_restricted ? (
            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <Ban className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-red-700">Account Restricted</span>
              </div>
              <p className="text-sm text-red-600 mb-2">{settings.restriction_reason}</p>
              {settings.restricted_until && (
                <p className="text-xs text-red-500">
                  Until: {new Date(settings.restricted_until).toLocaleString()}
                </p>
              )}
              <Button 
                size="sm" 
                variant="outline" 
                className="mt-3 text-red-600 border-red-300"
                onClick={() => toggleRestriction(false)}
              >
                Remove Restriction
              </Button>
            </div>
          ) : (
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-700">Account Active</span>
              </div>
              <p className="text-sm text-green-600">{teen.name} can book rides normally</p>
              <Button 
                size="sm" 
                variant="outline" 
                className="mt-3 text-amber-600 border-amber-300"
                onClick={() => toggleRestriction(true)}
              >
                <Ban className="w-4 h-4 mr-2" /> Restrict Account
              </Button>
            </div>
          )}

          {/* Quick Settings */}
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm">Allowed Hours</span>
              <span className="text-sm font-medium">
                {settings?.allowed_start_hour || 6}:00 - {settings?.allowed_end_hour || 22}:00
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Daily Limit</span>
              <span className="text-sm font-medium">${settings?.daily_spending_limit || 50}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Audio Recording</span>
              <Badge className="bg-green-100 text-green-700">Mandatory</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Approved Locations Component
const ApprovedLocations = ({ teenId, token }) => {
  const [locations, setLocations] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    lat: 0,
    lng: 0,
    is_preset: false
  });
  const [settings, setSettings] = useState({});

  useEffect(() => {
    fetchSettings();
  }, [teenId]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/parent/teen/${teenId}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSettings(data.settings || {});
      setLocations(data.settings?.approved_locations || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const addLocation = async () => {
    if (!newLocation.name || !newLocation.address) {
      toast.error('Name and address are required');
      return;
    }

    try {
      const res = await fetch(`${API}/parent/teen/${teenId}/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newLocation,
          lat: 33.749, // Default Atlanta coords - in production would geocode
          lng: -84.388
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Location added');
        setShowAdd(false);
        setNewLocation({ name: '', address: '', lat: 0, lng: 0, is_preset: false });
        fetchSettings();
      }
    } catch (error) {
      toast.error('Failed to add location');
    }
  };

  const removeLocation = async (locationId) => {
    try {
      await fetch(`${API}/parent/teen/${teenId}/locations/${locationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Location removed');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to remove location');
    }
  };

  const toggleRestrictToApproved = async (value) => {
    try {
      const res = await fetch(`${API}/parent/teen/${teenId}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          settings: { ...settings, restrict_to_approved_only: value }
        })
      });
      if (res.ok) {
        toast.success(value ? 'Restricted to approved locations only' : 'All locations allowed');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#1E4DB7]" />
          Approved Locations
        </CardTitle>
        <CardDescription>
          Set approved pickup and dropoff locations for your teen
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Restrict Toggle */}
        <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
          <div>
            <p className="font-medium text-amber-800">Restrict to approved locations only</p>
            <p className="text-sm text-amber-600">Teen can only book rides to/from these locations</p>
          </div>
          <Switch
            checked={settings.restrict_to_approved_only || false}
            onCheckedChange={toggleRestrictToApproved}
          />
        </div>

        {/* Preset Locations */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Home className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-700">Home</span>
              <Badge className="bg-blue-200 text-blue-700 text-xs">Preset</Badge>
            </div>
            <p className="text-sm text-blue-600">
              {locations.find(l => l.name === 'Home')?.address || 'Not set'}
            </p>
          </div>
          <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
            <div className="flex items-center gap-2 mb-2">
              <School className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-purple-700">School</span>
              <Badge className="bg-purple-200 text-purple-700 text-xs">Preset</Badge>
            </div>
            <p className="text-sm text-purple-600">
              {locations.find(l => l.name === 'School')?.address || 'Not set'}
            </p>
          </div>
        </div>

        {/* Other Locations */}
        <div className="space-y-2">
          {locations.filter(l => l.name !== 'Home' && l.name !== 'School').map((location) => (
            <div key={location.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="font-medium">{location.name}</p>
                  <p className="text-sm text-slate-500">{location.address}</p>
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="text-red-500 hover:text-red-700"
                onClick={() => removeLocation(location.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        <Button
          variant="outline"
          className="w-full border-dashed"
          onClick={() => setShowAdd(true)}
        >
          <Plus className="w-4 h-4 mr-2" /> Add Approved Location
        </Button>

        {/* Add Location Dialog */}
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Approved Location</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Location Name</Label>
                <Select
                  value={newLocation.name}
                  onValueChange={(v) => setNewLocation({ ...newLocation, name: v, is_preset: v === 'Home' || v === 'School' })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select or type a name" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Home">Home</SelectItem>
                    <SelectItem value="School">School</SelectItem>
                    <SelectItem value="Work">Work</SelectItem>
                    <SelectItem value="Grandparents">Grandparents</SelectItem>
                    <SelectItem value="Friend's House">Friend's House</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={newLocation.address}
                  onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                  placeholder="Enter full address"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={addLocation} className="bg-[#1E4DB7]">Add Location</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Spending Limits Component
const SpendingLimits = ({ teenId, token }) => {
  const [settings, setSettings] = useState({
    allowed_start_hour: 6,
    allowed_end_hour: 22,
    daily_spending_limit: 50,
    weekly_spending_limit: 200,
    per_ride_limit: 30,
    notify_on_ride_book: true,
    notify_on_ride_start: true,
    notify_on_ride_complete: true,
    notify_on_route_deviation: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [teenId]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/parent/teen/${teenId}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.settings) {
        setSettings(prev => ({ ...prev, ...data.settings }));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/parent/teen/${teenId}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ settings })
      });
      if (res.ok) {
        toast.success('Settings saved');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Allowed Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#1E4DB7]" />
            Allowed Ride Hours
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <Select
                value={String(settings.allowed_start_hour)}
                onValueChange={(v) => setSettings({ ...settings, allowed_start_hour: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <Select
                value={String(settings.allowed_end_hour)}
                onValueChange={(v) => setSettings({ ...settings, allowed_end_hour: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-slate-500">
            Note: System also enforces 11:30 PM - 5:00 AM restriction for all teen riders
          </p>
        </CardContent>
      </Card>

      {/* Spending Limits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#1E4DB7]" />
            Spending Limits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Per Ride Limit</Label>
              <span className="font-semibold">${settings.per_ride_limit}</span>
            </div>
            <Slider
              value={[settings.per_ride_limit]}
              onValueChange={([v]) => setSettings({ ...settings, per_ride_limit: v })}
              max={100}
              min={10}
              step={5}
            />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Daily Limit</Label>
              <span className="font-semibold">${settings.daily_spending_limit}</span>
            </div>
            <Slider
              value={[settings.daily_spending_limit]}
              onValueChange={([v]) => setSettings({ ...settings, daily_spending_limit: v })}
              max={200}
              min={20}
              step={10}
            />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Weekly Limit</Label>
              <span className="font-semibold">${settings.weekly_spending_limit}</span>
            </div>
            <Slider
              value={[settings.weekly_spending_limit]}
              onValueChange={([v]) => setSettings({ ...settings, weekly_spending_limit: v })}
              max={500}
              min={50}
              step={25}
            />
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#1E4DB7]" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            { key: 'notify_on_ride_book', label: 'When ride is booked' },
            { key: 'notify_on_ride_start', label: 'When ride starts' },
            { key: 'notify_on_ride_complete', label: 'When ride completes' },
            { key: 'notify_on_route_deviation', label: 'Route deviation alerts' }
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-sm">{label}</span>
              <Switch
                checked={settings[key]}
                onCheckedChange={(v) => setSettings({ ...settings, [key]: v })}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={saveSettings} disabled={saving} className="w-full bg-[#1E4DB7]">
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
};

// Ride History Component
const RideHistory = ({ teenId, teenName, token }) => {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    fetchHistory();
  }, [teenId, days]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/parent/teen/${teenId}/rides?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRides(data.rides || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#1E4DB7]" />
            Ride History for {teenName}
          </CardTitle>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : rides.length === 0 ? (
          <div className="text-center py-8 text-slate-500">No rides in this period</div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-3">
              {rides.map((ride) => (
                <div key={ride.id} className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">
                      {new Date(ride.created_at).toLocaleDateString()} at {new Date(ride.created_at).toLocaleTimeString()}
                    </span>
                    <Badge className={
                      ride.status === 'completed' ? 'bg-green-100 text-green-700' :
                      ride.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-blue-100 text-blue-700'
                    }>
                      {ride.status}
                    </Badge>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p><strong>From:</strong> {ride.pickup_location?.address}</p>
                    <p><strong>To:</strong> {ride.dropoff_location?.address}</p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t">
                      <span>Fare: ${ride.fare?.toFixed(2)}</span>
                      {ride.rider_rating && (
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          {ride.rider_rating}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

// Weekly Report Component
const WeeklyReport = ({ teenId, token }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [teenId]);

  const fetchReport = async () => {
    try {
      const res = await fetch(`${API}/parent/teen/${teenId}/report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setReport(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading report...</div>;
  }

  if (!report) {
    return <div className="text-center py-8 text-slate-500">No report available</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#1E4DB7]" />
          Weekly Report Card for {report.teen_name}
        </CardTitle>
        <CardDescription>
          {new Date(report.period_start).toLocaleDateString()} - {new Date(report.period_end).toLocaleDateString()}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Rating Alert */}
        {report.is_restricted && (
          <div className="mb-6 p-4 bg-red-50 rounded-xl border border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-700">Account Restricted</span>
            </div>
            <p className="text-sm text-red-600">
              Rating dropped below 3.5. Rides restricted to School/Home until {new Date(report.restricted_until).toLocaleDateString()}
            </p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-blue-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-blue-700">{report.total_rides}</p>
            <p className="text-sm text-blue-600">Total Rides</p>
          </div>
          <div className="p-4 bg-green-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-green-700">{report.completed_rides}</p>
            <p className="text-sm text-green-600">Completed</p>
          </div>
          <div className="p-4 bg-amber-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-amber-700">${report.total_spent.toFixed(2)}</p>
            <p className="text-sm text-amber-600">Total Spent</p>
          </div>
          <div className={`p-4 rounded-xl text-center ${report.average_rating >= 4 ? 'bg-green-50' : report.average_rating >= 3.5 ? 'bg-amber-50' : 'bg-red-50'}`}>
            <div className="flex items-center justify-center gap-1">
              <Star className={`w-5 h-5 ${report.average_rating >= 4 ? 'text-green-500' : report.average_rating >= 3.5 ? 'text-amber-500' : 'text-red-500'} fill-current`} />
              <p className={`text-2xl font-bold ${report.average_rating >= 4 ? 'text-green-700' : report.average_rating >= 3.5 ? 'text-amber-700' : 'text-red-700'}`}>
                {report.average_rating.toFixed(1)}
              </p>
            </div>
            <p className={`text-sm ${report.average_rating >= 4 ? 'text-green-600' : report.average_rating >= 3.5 ? 'text-amber-600' : 'text-red-600'}`}>
              Avg Rating
            </p>
          </div>
        </div>

        {/* Rating Trend */}
        <div className="p-4 bg-slate-50 rounded-xl">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            {report.average_rating >= 4 ? (
              <TrendingUp className="w-5 h-5 text-green-500" />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-500" />
            )}
            Rating Status
          </h4>
          <p className="text-sm text-slate-600">
            {report.average_rating >= 4.5 ? "Excellent! Keep up the great behavior." :
             report.average_rating >= 4 ? "Good standing. Minor improvements possible." :
             report.average_rating >= 3.5 ? "Needs improvement. Review ride etiquette." :
             "Below acceptable. Account may be restricted."}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ParentDashboard;
